import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';

// Declare Quill types to avoid TypeScript errors
declare global {
  interface Window {
    Quill: any;
  }
}

export interface HtmlEditorHandle {
  getContent: () => string;
  setContent: (content: string) => void;
}

interface HtmlEditorProps {
  value?: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  height?: string;
}

const HtmlEditor = forwardRef<HtmlEditorHandle, HtmlEditorProps>(({
  value = '',
  onChange,
  placeholder = 'Enter your message content...',
  height = '200px'
}, ref) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const quillRef = useRef<any>(null);
  const initializedRef = useRef(false);
  const [isQuillReady, setIsQuillReady] = useState(false);

  useImperativeHandle(ref, () => ({
    getContent: () => {
      if (quillRef.current) {
        return quillRef.current.root.innerHTML;
      }
      return '';
    },
    setContent: (content: string) => {
      if (quillRef.current) {
        quillRef.current.root.innerHTML = content;
      }
    }
  }));

  useEffect(() => {
    const loadQuill = async () => {
      // Check if Quill is already loaded
      if (window.Quill && !initializedRef.current && editorRef.current) {
        const toolbarOptions = [
          ['bold', 'italic', 'underline', 'strike'],
          ['blockquote', 'code-block'],
          [{ 'header': 1 }, { 'header': 2 }],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'script': 'sub'}, { 'script': 'super' }],
          [{ 'indent': '-1'}, { 'indent': '+1' }],
          [{ 'direction': 'rtl' }],
          [{ 'size': ['small', false, 'large', 'huge'] }],
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'font': [] }],
          [{ 'align': [] }],
          ['clean'],
          ['link', 'image']
        ];

        quillRef.current = new window.Quill(editorRef.current, {
          theme: 'snow',
          modules: {
            toolbar: toolbarOptions
          },
          placeholder: placeholder
        });

        // Listen for text changes
        quillRef.current.on('text-change', () => {
          if (onChange && quillRef.current) {
            const html = quillRef.current.root.innerHTML;
            onChange(html);
          }
        });

        initializedRef.current = true;
        setIsQuillReady(true); // Trigger the value update effect
      }
    };

    // Load Quill if not already loaded
    if (!window.Quill) {
      // Create script and link elements for Quill
      const link = document.createElement('link');
      link.href = 'https://cdn.quilljs.com/1.3.6/quill.snow.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://cdn.quilljs.com/1.3.6/quill.min.js';
      script.onload = loadQuill;
      document.head.appendChild(script);
    } else {
      loadQuill();
    }

    return () => {
      if (quillRef.current) {
        quillRef.current = null;
      }
    };
  }, [onChange, placeholder]);

  // Update content when value prop changes OR when Quill becomes ready
  // This handles both initial load and subsequent updates
  useEffect(() => {
    // Wait for Quill to be initialized
    if (!isQuillReady || !quillRef.current || value === undefined || value === null) {
      return;
    }

    const currentContent = quillRef.current.root.innerHTML;

    // Normalize HTML for comparison - Quill adds <p><br></p> for empty content
    const normalizedCurrent = currentContent?.trim() === '<p><br></p>' ? '' : currentContent?.trim();
    const normalizedValue = value?.trim() === '<p><br></p>' ? '' : value?.trim();

    // Only update if content is actually different
    if (normalizedCurrent !== normalizedValue) {
      quillRef.current.root.innerHTML = value;
    }
  }, [value, isQuillReady]);

  return (
    <div className="html-editor">
      <div 
        ref={editorRef} 
        style={{ height }}
        className="border border-gray-300 rounded-md"
      />
    </div>
  );
});

HtmlEditor.displayName = 'HtmlEditor';

export default HtmlEditor;